import React, { useEffect, useState } from 'react';

import { useAppSelector } from '../hooks/redux';
import { selectUser } from '../store/auth.slice';
import { ContentCopy } from '../components/icons/content-copy';
import { MuiRefreshIcon } from '../components/icons/mui-refresh';
import { useCreateReferalCodeMutation, useGetReferalCodesQuery, useGetReferalUsersQuery } from '../services/users.api';
import { maskEmail } from '../utils/maskEmail';
import { useGetPartnerReferralsQuery } from '../services/partners.api';
import { IconButton, Input, TabList } from '../shared/ui';

type TabKey = 'psychologists' | 'payments';

const ReferalManagement = () => {
  const [activeTab, setActiveTab] = useState<TabKey>('psychologists');
  const [referalCode, setReferalCode] = useState('');

  const {
    data: partnersPayment,
    isLoading: isPaymentLoading,
    refetch: refetchPayments,
    isFetching: isFetchingPayments,
  } = useGetPartnerReferralsQuery();
  const { data: referredUsers, refetch, isFetching, isLoading, error } = useGetReferalUsersQuery();
  const { data: referalCodes } = useGetReferalCodesQuery();
  const [createReferal] = useCreateReferalCodeMutation();

  const userData = useAppSelector(selectUser);

  const tabs: { key: TabKey; label: string }[] = [{ key: 'psychologists', label: 'Психологи' }];

  if (partnersPayment?.data?.length) {
    tabs.push({ key: 'payments', label: 'История начислений' });
  }

  const copyToClipboard = () => {
    const name = `${userData?.last_name || ''} ${userData?.first_name || ''} ${userData?.middle_name || ''}`.trim();
    const message = `Вас приглашает ${name} зарегистрироваться в системе ZiNGiZi.\nПерейдите по ссылке: https://lk.zingizi.pro/registration\nВведите Пригласительный ключ: ${referalCode}`;
    navigator.clipboard.writeText(message);
  };

  useEffect(() => {
    if (isFetching || isLoading) return;
    if (!referalCodes || referalCodes.codes.length === 0) {
      createReferal();
    } else {
      setReferalCode(referalCodes.codes.at(-1) || '');
    }
  }, [createReferal, referalCodes, isFetching, isLoading]);

  return (
    <div className="flex flex-col gap-[30px] w-full pb-[85px]">
      <p className="text-title">
        <strong>Приглашайте коллег-психологов в ZiNGiZi и получайте бонусы!</strong>
        <br />
        <br />
        За каждую регистрацию по вашему <strong>Пригласительному ключу</strong> вы получаете{' '}
        <strong>100 бонусных рублей</strong>. <br />
        Каждому зарегистрированному по вашему ключу психологу также будет начислено <strong>100 бонусных рублей</strong>
        .
        <br />
        <br />
        Курс: 1 бонусный рубль = 1 рубль. <br />
        <strong>Бонусными рублями</strong> можно оплатить часть стоимости подписки, обучающих курсов и других услуг на
        ZiNGiZi.
      </p>

      <div className="flex flex-wrap items-center gap-2.5 mb-6">
        <span>Ваш ключ:</span>
        <div className="w-40">
          <Input size="md" aria-label="Ваш ключ" value={referalCode} readOnly className="font-mono" />
        </div>
        <IconButton label="Скопировать ключ" variant="primary" size="md" onClick={copyToClipboard}>
          <ContentCopy className="w-[18px] h-[18px]" fill="currentColor" />
        </IconButton>
      </div>

      <p className="text-subtitle">Психологи открывшие ZiNGiZi Вашим ключом:</p>

      <TabList
        className="mb-4"
        items={tabs.map((t) => ({ id: t.key, label: t.label }))}
        value={activeTab}
        onChange={setActiveTab}
      />

      {activeTab === 'payments' && (
        <div className="overflow-x-auto">
          <table className="w-full text-ui text-ink-muted">
            <thead>
              <tr className="bg-line uppercase text-ui-xs-strong tracking-wide">
                <th className="py-[10px] px-[20px] border-b border-line-strong text-left">Email</th>
                <th className="py-[10px] px-[20px] border-b border-line-strong text-left">Имя</th>
                <th className="py-[10px] px-[20px] border-b border-line-strong text-left">
                  Название оплаченного тарифа
                </th>
                <th className="py-[10px] px-[20px] border-b border-line-strong text-left">Вознаграждение (10%)</th>
                <th className="py-[10px] px-[20px] border-b border-line-strong text-left">
                  <div className="flex items-center justify-between gap-1">
                    Дата оплаты
                    <IconButton label="Обновить" className="-m-1.5" onClick={refetchPayments}>
                      <MuiRefreshIcon className={`w-[20px] h-[20px] ${isFetchingPayments ? 'animate-spin' : ''}`} />
                    </IconButton>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {partnersPayment?.data?.map((partner, idx) => (
                <tr key={idx}>
                  <td className="py-[10px] px-[20px] border-b border-line-strong text-ellipsis whitespace-nowrap">
                    {maskEmail(partner.email_masked)}
                  </td>
                  <td className="py-[10px] px-[20px] border-b border-line-strong">{partner.full_name}</td>
                  <td className="py-[10px] px-[20px] border-b border-line-strong text-ellipsis whitespace-nowrap">
                    {partner.plan_name}
                  </td>
                  <td className="py-[10px] px-[20px] border-b border-line-strong text-ellipsis whitespace-nowrap">
                    {partner.reward}
                  </td>
                  <td className="py-[10px] px-[20px] border-b border-line-strong">
                    {new Date(partner.created_at).toLocaleDateString('ru-RU')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {activeTab === 'psychologists' && (
        <>
          {referredUsers?.users.length === 0 ? (
            <div className="text-ui text-ink-muted">Пока никто не зарегистрировался по вашему ключу.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-ui text-ink-muted">
                <thead>
                  <tr className="bg-line uppercase text-ui-xs-strong tracking-wide">
                    <th className="py-[10px] px-[20px] border-b border-line-strong text-left">Email</th>
                    <th className="py-[10px] px-[20px] border-b border-line-strong text-left">Имя и Фамилия</th>
                    <th className="py-[10px] px-[20px] border-b border-line-strong text-left">Дата регистрации</th>
                    <th className="py-[10px] px-[20px] border-b border-line-strong text-left">
                      <div className="flex items-center justify-between gap-1">
                        Исп. ключ
                        <IconButton label="Обновить" className="-m-1.5" onClick={refetch}>
                          <MuiRefreshIcon className={`w-[20px] h-[20px] ${isFetching ? 'animate-spin' : ''}`} />
                        </IconButton>
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {referredUsers?.users.map((user, idx) => (
                    <tr key={idx}>
                      <td className="py-[10px] px-[20px] border-b border-line-strong text-ellipsis whitespace-nowrap">
                        {maskEmail(user.email)}
                      </td>
                      <td className="py-[10px] px-[20px] border-b border-line-strong">{`${user.firstName} ${user.lastNameMasked}`}</td>
                      <td className="py-[10px] px-[20px] border-b border-line-strong">
                        {new Date(user.registrationDate).toLocaleDateString('ru-RU')}
                      </td>
                      <td className="py-[10px] px-[20px] border-b border-line-strong text-ellipsis whitespace-nowrap">
                        {user.usedReferalCode}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ReferalManagement;
